import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box } from '@mui/material'
import {
  LineChart as BaseLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { chartWrapper, tooltipStyles, tooltipLabelStyles } from '../styles'

type SeriesConfig = { key: string; name: string; color: string }

type MultiLineChartProps = {
  data: Array<Record<string, any>>
  series: SeriesConfig[]
  xDataKey: string
  yLabel?: string
  xLabel?: string
  hideLegend?: boolean
  hideXAxisLabels?: boolean
  hideYAxisLabels?: boolean
  xAxisTooltip?: React.ComponentType<any> | ((props: any) => React.ReactNode)
  usePercentageScale?: boolean
}

const PCT_BUFFER = 0.15 // 15% headroom
const PCT_CEILING = 100 // cap (change/remove if you need >100%)
const Y_AXIS_BUFFER = 0.5
const ROTATE_THRESHOLD = 2

export const PAD_L = '__pad_left__'
export const PAD_R = '__pad_right__'
export const isPaddedLabel = (label: any) => label === PAD_L || label === PAD_R

const DefaultTooltip = ({
  active,
  payload,
  label,
  usePercentageScale,
}: any) => {
  if (!active || !payload?.length || isPaddedLabel(label)) return null
  const cleanedPayload = payload.map((item) => ({
    ...item,
    name: item.name.replace(/^Wheat\s*/i, ''), // remove 'Wheat ' (case-insensitive)
  }))
  return (
    <div style={{ ...tooltipStyles, padding: 8 }}>
      <div style={{ ...tooltipLabelStyles, fontWeight: 'bold' }}>{label}</div>
      {cleanedPayload.map((p: any) => {
        const name = p.dataKey
        const val = p.value
        const std = p?.cleanedPayload?.[`${name}_std`]
        if (val == null || Number.isNaN(val)) return null

        const percentSymbol = usePercentageScale ? '%' : ''

        const text =
          std != null
            ? `${Number(val).toFixed(2)}${percentSymbol} (±${Number(std).toFixed(2)}${percentSymbol})`
            : `${Number(val).toFixed(2)}${percentSymbol}`

        return (
          <div key={name} style={tooltipLabelStyles}>
            {`${p.name}: ${text}`}
          </div>
        )
      })}
    </div>
  )
}

export const MultiLineChart = ({
  data,
  series,
  xDataKey,
  yLabel,
  xLabel,
  hideLegend = false,
  hideXAxisLabels = false,
  hideYAxisLabels = false,
  xAxisTooltip,
  usePercentageScale = false,
}: MultiLineChartProps) => {
  const [xAxisHoverLabel, setXAxisHoverLabel] = useState<string | null>(null)
  const [xAxisHoverPosition, setXAxisHoverPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  if (!data?.length) return null

  const realRows = useMemo(
    () => data.filter((r) => series.some((s) => typeof r[s.key] === 'number')),
    [data, series]
  )
  const onlyOneRealRow = realRows.length === 1
  const xTicks = useMemo(
    () => realRows.map((r) => r[xDataKey]),
    [realRows, xDataKey]
  )

  const [yMin, yMax] = useMemo(() => {
    // If using percentage scale, base the domain on data's max, not a fixed 100
    if (usePercentageScale) {
      let min = Infinity
      let max = -Infinity

      for (const row of realRows) {
        for (const s of series) {
          const v = row[s.key]
          if (typeof v === 'number' && !Number.isNaN(v)) {
            min = Math.min(min, v)
            max = Math.max(max, v)
          }
        }
      }

      if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 0]

      // add ±15% buffer around the actual [min, max]
      const range = Math.max(0, max - min)
      const buffer = range * PCT_BUFFER
      const bufferedMax = Math.min(PCT_CEILING, max + buffer)
      const bufferedMin = Math.max(0, min - buffer)

      // round both to “nice” values (nearest 5)
      const roundedMax = Math.ceil(bufferedMax / 5) * 5
      const roundedMin = Math.floor(bufferedMin / 5) * 5

      return [roundedMin, roundedMax]
    }

    let min = Infinity
    let max = -Infinity
    realRows.forEach((row) => {
      series.forEach((s) => {
        const v = row[s.key]
        if (typeof v === 'number') {
          min = Math.min(min, v)
          max = Math.max(max, v)
        }
      })
    })
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 0]

    const bufferedMax = Math.ceil(max + Y_AXIS_BUFFER)
    const bufferedMin = Math.max(Math.floor(min - Y_AXIS_BUFFER), 0)

    if (bufferedMax <= bufferedMin) {
      // minimal visible span
      return [0, bufferedMin + 1]
    }

    return [bufferedMin, bufferedMax]
  }, [realRows, series, usePercentageScale])

  const paddedData = useMemo(() => {
    if (!onlyOneRealRow) return data
    const single = realRows[0]
    return [
      { [xDataKey]: PAD_L, padded: true },
      single,
      { [xDataKey]: PAD_R, padded: true },
    ]
  }, [data, realRows, xDataKey, onlyOneRealRow])

  const tickStyle = { fontSize: 12 }

  const rafIdRef = useRef<number | null>(null)
  const pendingPosRef = useRef<{ x: number; y: number } | null>(null)
  const hideTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
      if (hideTimerRef.current != null) clearTimeout(hideTimerRef.current)
    }
  }, [])

  const schedulePos = (x: number, y: number) => {
    pendingPosRef.current = { x, y }
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(() => {
        if (pendingPosRef.current) setXAxisHoverPosition(pendingPosRef.current)
        rafIdRef.current = null
      })
    }
  }

  const clearHoverSoon = (delay = 80) => {
    if (hideTimerRef.current != null) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = window.setTimeout(() => {
      setXAxisHoverLabel(null)
      setXAxisHoverPosition(null)
    }, delay) as unknown as number
  }

  const cancelHide = () => {
    if (hideTimerRef.current != null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }
  const shouldRotateTicks = !hideXAxisLabels && xTicks.length > ROTATE_THRESHOLD
  const LABEL_ROOM = shouldRotateTicks ? 40 : 20 // space for angled labels
  const LEGEND_ROOM = !hideLegend ? 40 : 0
  const bottomMargin = 20

  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props
    if (isPaddedLabel(payload.value) || hideXAxisLabels) return null

    const rotate = shouldRotateTicks

    return (
      <g
        transform={`translate(${x},${y})`}
        onMouseLeave={() => clearHoverSoon(100)}
        onMouseEnter={() => cancelHide()}
      >
        <rect
          x={-50}
          y={-24}
          width={100}
          height={44}
          fill="transparent"
          pointerEvents="all"
          onMouseEnter={(e) => {
            if (!xAxisTooltip) return
            setXAxisHoverLabel(payload.value)
            schedulePos(e.clientX, e.clientY)
          }}
          onMouseMove={(e) => {
            if (xAxisTooltip && xAxisHoverLabel === payload.value) {
              schedulePos(e.clientX, e.clientY)
            }
          }}
          onMouseLeave={() => {
            clearHoverSoon(80)
          }}
          style={{ cursor: xAxisTooltip ? 'pointer' : 'default' }}
        />
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor={rotate ? 'start' : 'middle'}
          fill="#666"
          fontSize={12}
          pointerEvents="none"
          transform={rotate ? `rotate(45)` : undefined}
        >
          {payload.value}
        </text>
      </g>
    )
  }

  /** Adapter that lets us pass either a component or a render fn to <Tooltip content>. */
  const tooltipAdapter = (props: any) => {
    if (isPaddedLabel(props?.label)) return null

    const isDataPoint =
      props.payload &&
      props.payload.length > 0 &&
      props.payload.some((p: any) => p.value !== undefined)

    if (isDataPoint) {
      return (
        <DefaultTooltip {...props} usePercentageScale={usePercentageScale} />
      )
    }

    return null
  }

  const renderXAxisTooltip = () => {
    if (!xAxisHoverLabel || !xAxisHoverPosition) return null
    const tooltipProps = {
      label: xAxisHoverLabel,
      payload: data.filter((d) => d[xDataKey] === xAxisHoverLabel),
    }
    const tooltipContent =
      typeof xAxisTooltip === 'function'
        ? (xAxisTooltip as any)(tooltipProps)
        : React.createElement(
            xAxisTooltip as React.ComponentType<any>,
            tooltipProps
          )

    if (!tooltipContent) return null

    return (
      <div
        style={{
          position: 'fixed',
          left: xAxisHoverPosition.x,
          top: xAxisHoverPosition.y - 200,
          pointerEvents: 'none',
          transform: 'translateX(-50%)',
          zIndex: 999999,
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid rgba(0,0,0,0.15)',
          borderRadius: 6,
          padding: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          color: '#111',
        }}
      >
        {tooltipContent}
      </div>
    )
  }

  return (
    <>
      <Box
        sx={{ ...chartWrapper, height: { xs: 250, sm: 350, md: 450, lg: 550 } }}
        onMouseLeave={() => clearHoverSoon(80)}
        onMouseEnter={() => cancelHide()}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BaseLineChart
            data={paddedData}
            margin={{
              top: 20,
              right: 40,
              left: 5,
              bottom: bottomMargin,
            }}
          >
            <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
            <XAxis
              dataKey={xDataKey}
              ticks={xTicks}
              tick={<CustomXAxisTick />}
              label={
                xLabel
                  ? { value: xLabel, position: 'insideBottom', offset: -10 }
                  : undefined
              }
              angle={data.length > 2 ? 45 : 0}
            />
            <YAxis
              tick={hideYAxisLabels ? false : tickStyle}
              domain={[yMin, yMax]}
              label={
                yLabel
                  ? { value: yLabel, angle: -90, position: 'insideLeft' }
                  : undefined
              }
              tickFormatter={(value: number) =>
                usePercentageScale ? `${value}%` : `${value}`
              }
              axisLine={true}
            />
            <Tooltip content={tooltipAdapter} />
            {!hideLegend && (
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ paddingTop: LEGEND_ROOM }}
              />
            )}
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
                connectNulls
              />
            ))}
          </BaseLineChart>
        </ResponsiveContainer>
      </Box>
      {xAxisHoverLabel &&
        xAxisHoverPosition &&
        xAxisTooltip &&
        createPortal(renderXAxisTooltip(), document.body)}
    </>
  )
}
